/**
 * Global Cache Reader Utility
 * Functions to read from the global rankings cache
 */

import { db } from '../firebase';
import { doc, getDoc, collection, getDocs, query, where, setDoc } from 'firebase/firestore';

// 🚀 Module-level in-memory cache — avoids re-reading the same dancer's Firestore doc within a session
const dancerRankingsMemoryCache = new Map();

/**
 * Get cached rankings for a specific category
 */
export async function getCachedCategoryRankings(discipline, category, dance = null) {
  try {
    const cacheKey = dance 
      ? `${discipline}_${category}_${dance}`
      : `${discipline}_${category}`;
    
    const docRef = doc(db, 'globalRankingsCache', cacheKey);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      console.log(`✅ Found cached rankings for ${discipline} - ${category}${dance ? ` (${dance})` : ''}: ${data.rankings?.length || 0} dancers`);
      return data;
    } else {
      console.log(`⚠️ No cached rankings found for ${discipline} - ${category}${dance ? ` (${dance})` : ''}`);
      return null;
    }
  } catch (error) {
    console.error('❌ Error reading cached category rankings:', error);
    return null;
  }
}

/**
 * Get cached dancer profile with enhanced fallback
 */
export async function getCachedDancerRankings(dancerName) {
  try {
    const sanitizedName = dancerName.replace(/[/\\#.]/g, '_');
    
    // 🚀 Check in-memory session cache first (avoids redundant Firestore reads within same page session)
    if (dancerRankingsMemoryCache.has(sanitizedName)) {
      console.log(`✅ Found cached profile for ${dancerName} from in-memory session cache`);
      return dancerRankingsMemoryCache.get(sanitizedName);
    }
    
    // Try to get from enhanced cache first
    const docRef = doc(db, 'dancerRankingsCache', sanitizedName);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      console.log(`✅ Found cached profile for ${dancerName} from global cache`);
      
      // Transform the enhanced cache format to match expected structure
      const transformedData = transformEnhancedCacheData(data);
      // 🚀 Store in memory cache for fast repeat access within the same session
      dancerRankingsMemoryCache.set(sanitizedName, transformedData);
      return transformedData;
    }
    
    // Fallback to old cache system
    console.log(`⚠️ No global cache found for ${dancerName}, checking old cache...`);
    const oldCacheRef = doc(db, 'cachedDancerRankings', sanitizedName);
    const oldCacheSnap = await getDoc(oldCacheRef);
    
    if (oldCacheSnap.exists()) {
      console.log(`✅ Found cached profile for ${dancerName} from old cache`);
      const oldData = oldCacheSnap.data();
      // 🚀 Store in memory cache for fast repeat access within the same session
      dancerRankingsMemoryCache.set(sanitizedName, oldData);
      return oldData;
    }
    
    console.log(`⚠️ No cached profile found for ${dancerName}`);
    return null;
    
  } catch (error) {
    console.error('❌ Error reading cached dancer profile:', error);
    return null;
  }
}

/**
 * Transform enhanced cache data to match expected DancerProfile format
 */
function transformEnhancedCacheData(cacheData) {
  if (!cacheData.categories) {
    return cacheData; // Return as-is if it's old format
  }
  
  // Transform categories object to disciplineOverview array
  const disciplineOverview = [];
  const disciplines = new Set();
  const categories = new Set();
  
  // Extract all the Star Ranking data from cached categories
  let totalActivePoints = 0;
  let bestOverallRank = null;
  let currentOverallRank = null;
  let currentDaysHeld = 0;
  let bestDaysHeld = 0;
  
  for (const [categoryKey, categoryData] of Object.entries(cacheData.categories)) {
    disciplineOverview.push({
      discipline: categoryData.discipline,
      category: categoryData.category,
      dance: categoryData.dance,
      // Basic ranking data
      totalPoints: categoryData.totalPoints || 0,
      currentRank: categoryData.currentRank,
      resultsCount: categoryData.resultsCount || 0,
      // Enhanced Star Ranking data
      activePoints: categoryData.activePoints || categoryData.totalPoints || 0,
      daysHeld: categoryData.currentDaysHeld || 0,
      bestRank: categoryData.bestRank,
      bestDaysHeld: categoryData.bestDaysHeld || 0,
      hasCurrentRank: categoryData.hasStarRanking || false,
      totalDancers: categoryData.totalDancers || 0,
      lastCalculated: categoryData.lastCalculated || cacheData.lastCalculated
    });
    
    disciplines.add(categoryData.discipline);
    categories.add(categoryData.category);
    
    // Aggregate stats for profile overview
    totalActivePoints += (categoryData.activePoints || categoryData.totalPoints || 0);
    
    if (categoryData.currentRank && (!currentOverallRank || categoryData.currentRank < currentOverallRank)) {
      currentOverallRank = categoryData.currentRank;
      currentDaysHeld = categoryData.currentDaysHeld || 0;
    }
    
    if (categoryData.bestRank && (!bestOverallRank || categoryData.bestRank < bestOverallRank)) {
      bestOverallRank = categoryData.bestRank;
      bestDaysHeld = categoryData.bestDaysHeld || 0;
    }
  }
  
  // Return transformed data in expected format
  return {
    // Profile overview data
    dancerName: cacheData.dancerName,
    studio: cacheData.studio || cacheData.currentStudio || null, // Don't default to "-" here
    results: cacheData.results || [],
    top4: cacheData.top4 || [],
    totalPoints: totalActivePoints,
    // Enhanced Star Ranking data
    currentRank: currentOverallRank,
    bestRank: bestOverallRank,
    daysHeld: currentDaysHeld,
    bestDaysHeld: bestDaysHeld,
    // Structure data
    disciplines: Array.from(disciplines),
    categories: Array.from(categories),
    disciplineOverview: disciplineOverview,
    compNameMap: cacheData.compNameMap || {},
    lastCalculated: cacheData.lastCalculated,
    // Achievement Badges
    achievementBadges: cacheData.achievementBadges || [],
    // Eternal Cup Winner Status
    isEternalCupWinner: cacheData.isEternalCupWinner || { isWinner: false },
    // Cache metadata
    isEnhancedCache: true,
    cacheVersion: '2.0'
  };
}

/**
 * Get cache metadata and status
 */
export async function getCacheMetadata() {
  try {
    const docRef = doc(db, 'globalRankingsCache', '_metadata');
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        ...data,
        lastCalculatedAge: data.lastCalculated ? Date.now() - data.lastCalculated.toMillis() : null,
        isStale: data.lastCalculated ? (Date.now() - data.lastCalculated.toMillis()) > (24 * 60 * 60 * 1000) : true
      };
    } else {
      return {
        exists: false,
        isStale: true
      };
    }
  } catch (error) {
    console.error('❌ Error reading cache metadata:', error);
    return {
      exists: false,
      isStale: true,
      error: error.message
    };
  }
}

/**
 * Get all cached category combinations
 */
export async function getAllCachedCategories() {
  try {
    const snapshot = await getDocs(collection(db, 'globalRankingsCache'));
    const categories = [];
    
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      if (doc.id !== '_metadata' && data.discipline && data.category) {
        categories.push({
          id: doc.id,
          discipline: data.discipline,
          category: data.category,
          dance: data.dance,
          totalDancers: data.totalDancers || 0,
          lastCalculated: data.lastCalculated
        });
      }
    });
    
    console.log(`✅ Found ${categories.length} cached categories`);
    return categories.sort((a, b) => {
      // Sort by discipline, then category, then dance
      if (a.discipline !== b.discipline) {
        return a.discipline.localeCompare(b.discipline);
      }
      if (a.category !== b.category) {
        return a.category.localeCompare(b.category);
      }
      return (a.dance || '').localeCompare(b.dance || '');
    });
    
  } catch (error) {
    console.error('❌ Error reading cached categories:', error);
    return [];
  }
}

/**
 * Search for dancers in cached rankings
 */
export async function searchCachedDancers(searchTerm, limit = 20) {
  try {
    const snapshot = await getDocs(collection(db, 'dancerRankingsCache'));
    const dancers = [];
    
    const searchLower = searchTerm.toLowerCase();
    
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      if (data.dancerName && data.dancerName.toLowerCase().includes(searchLower)) {
        dancers.push({
          dancerName: data.dancerName,
          totalPoints: data.totalPoints || 0,
          disciplineCount: data.disciplineOverview?.length || 0,
          lastCalculated: data.lastCalculated
        });
      }
    });
    
    // Sort by total points descending
    dancers.sort((a, b) => b.totalPoints - a.totalPoints);
    
    console.log(`🔍 Found ${dancers.length} dancers matching "${searchTerm}"`);
    return dancers.slice(0, limit);
    
  } catch (error) {
    console.error('❌ Error searching cached dancers:', error);
    return [];
  }
}

/**
 * Get top dancers across all categories
 */
export async function getTopDancersFromCache(limit = 100) {
  try {
    const snapshot = await getDocs(collection(db, 'dancerRankingsCache'));
    const dancers = [];
    
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      if (data.dancerName && data.totalPoints > 0) {
        dancers.push({
          dancerName: data.dancerName,
          totalPoints: data.totalPoints,
          currentRank: data.currentRank,
          bestRank: data.bestRank,
          disciplineCount: data.disciplineOverview?.length || 0
        });
      }
    });
    
    // Sort by total points descending
    dancers.sort((a, b) => b.totalPoints - a.totalPoints);
    
    console.log(`🏆 Retrieved top ${Math.min(limit, dancers.length)} dancers from cache`);
    return dancers.slice(0, limit);
    
  } catch (error) {
    console.error('❌ Error getting top dancers from cache:', error);
    return [];
  }
}

/**
 * Check if cache needs refresh
 */
export async function shouldRefreshCache() {
  const metadata = await getCacheMetadata();
  
  if (!metadata.exists) {
    console.log('🔄 Cache doesn\'t exist, needs initial calculation');
    return true;
  }
  
  if (metadata.isStale) {
    console.log('🔄 Cache is stale (>24h), needs refresh');
    return true;
  }
  
  console.log('✅ Cache is fresh, no refresh needed');
  return false;
}

/**
 * Save enhanced dancer rankings (backwards compatible)
 */
export async function saveCachedDancerRankings(dancerName, data) {
  try {
    const sanitizedName = dancerName.replace(/[/\\#.]/g, '_');
    
    // Save to both new and old cache for compatibility
    const docRef = doc(db, 'dancerRankingsCache', sanitizedName);
    const oldDocRef = doc(db, 'cachedDancerRankings', sanitizedName);
    
    const cacheData = {
      ...data,
      lastCalculated: new Date(),
      calculationVersion: '2.0'
    };
    
    await Promise.all([
      setDoc(docRef, cacheData),
      setDoc(oldDocRef, cacheData)  // Keep old cache for fallback
    ]);
    
    console.log(`✅ Cached rankings saved for ${dancerName}`);
    return true;
    
  } catch (error) {
    console.error('❌ Error saving cached rankings:', error);
    return false;
  }
}
